// src/components/forms/InlineRowWrapper.tsx

import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Typography,
    useTheme
} from "@mui/material";
import { TableField } from "../../types/interfaces";
import { validateFieldValue } from "../../utils/validators";
import InlineFieldRenderer from "./InlineFieldRenderer";

interface Props {
    fields: TableField[];
    initialData?: Record<string, any>;
    onSave: (data: Record<string, any>) => void;
    onCancel: () => void;
}

/**
 * InlineRowWrapper displays a row of editable fields for inline creation.
 * It includes live validation, keyboard shortcut support, and theme-aware styles.
 */
const InlineRowWrapper: React.FC<Props> = ({
    fields,
    initialData = {},
    onSave,
    onCancel
}) => {
    const theme = useTheme();
    const [values, setValues] = useState<Record<string, any>>({});
    const [errors, setErrors] = useState<Record<string, string | undefined>>({});

    useEffect(() => {
        const init: Record<string, any> = {};
        fields.forEach(f => {
            init[f.fieldID] = initialData[f.fieldID] ?? "";
        });
        setValues(init);
    }, [fields, initialData]);

    const handleFieldChange = (fieldId: string, value: any) => {
        setValues(prev => ({ ...prev, [fieldId]: value }));
        setErrors(prev => ({ ...prev, [fieldId]: undefined }));
    };

    const validateAll = (): boolean => {
        const nextErrors: Record<string, string | undefined> = {};
        let isValid = true;

        for (const field of fields) {
            const result = validateFieldValue(field, values[field.fieldID]);
            if (!result.valid) {
                nextErrors[field.fieldID] = result.message;
                isValid = false;
            }
        }

        setErrors(nextErrors);
        return isValid;
    };

    const handleSave = () => {
        if (validateAll()) {
            onSave(values);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
        }
    };

    return (
        <Box
            onKeyDown={handleKeyDown}
            sx={{
                backgroundColor: theme.palette.mode === "dark" ? "#1d1d1d" : "#f8f8f8",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                padding: 2,
                boxShadow: theme.shadows[1],
                transition: "box-shadow 0.3s ease",
            }}
        >
            <Grid container spacing={2}>
                {fields.map((field, index) => (
                    <Grid item xs={12} sm={6} md={4} key={field.fieldID}>
                        <InlineFieldRenderer
                            field={field}
                            value={values[field.fieldID]}
                            onChange={handleFieldChange}
                            error={errors[field.fieldID]}
                            autoFocus={index === 0}
                            isEditing={true}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Action buttons */}
            <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
                <Button onClick={onCancel} variant="outlined">
                    Cancel (Esc)
                </Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Save (⏎)
                </Button>
            </Box>

            {/* Placeholder: Additional tooltips, history, or audit icons can go here */}
        </Box>
    );
};

export default InlineRowWrapper;
